import React from 'react'

const D_t = () => {
    return (
        <div className='container'>
            <h1>Data Types</h1>
            <p>While writing program in any language, you need to use various variables to store various 
                information. Variables are nothing but reserved memory locations to store values. This means
                 that when you create a variable you reserve some space in memory.</p>
            <p>You may like to store information of various data types like character, wide character, 
                     integer, floating point, double floating point, boolean etc. Based on the data type 
                     of a variable, the operating system allocates memory and decides what can be stored in the 
             reserved memory.</p>
             <h2>Primitive Built-in Types</h2>
             <p>C++ offers the programmer a rich assortment of built-in as well as user defined data types. 
                 Following table lists down seven basic C++ data types −</p>
                 <table class="table table-bordered" style={{textAlign:'center'}}>
<tbody><tr>
<th width="50%" style={{textAlign:'center'}}>Type</th>
<th style={{textAlign:'center'}}>Keyword</th>
</tr>
<tr>
<td>Boolean</td>
<td>bool</td>
</tr>
<tr>
<td>Character</td>
<td>char</td>
</tr>
<tr>
<td>Integer</td>
<td>int</td>
</tr>
<tr>
<td>Floating point</td>
<td>float</td>
</tr>
<tr>
<td>Double floating point</td>
<td>double</td>
</tr>
<tr>
<td>Valueless</td>
<td>void</td>
</tr>
<tr>
<td>Wide character</td>
<td>wchar_t</td>
</tr>
</tbody></table>
<p>Several of the basic types can be modified using one or more of these type modifiers −</p>
<ul class="list">
<li>signed</li>
<li>unsigned</li>
<li>short</li>
<li>long</li>
</ul>
<p>The following table shows the variable type, how much memory it takes to store the value in memory,
     and what is maximum and minimum value which can be stored in such type of variables.</p>
     <table class="table table-bordered" style={{textAlign:'center'}}>
<tbody><tr>
<th style={{textAlign:'center'}}>Type</th>
<th style={{textAlign:'center'}}>Typical Bit Width</th>
<th style={{textAlign:'center'}}>Typical Range</th>
</tr>
<tr>
<td>char</td>
<td>1byte</td>
<td>-127 to 127 or 0 to 255</td>
</tr>
<tr>
<td>unsigned char</td>
<td>1byte</td>
<td>0 to 255</td>
</tr>
<tr>
<td>signed char</td>
<td>1byte</td>
<td>-127 to 127</td>
</tr>
<tr>
<td>int</td>
<td>4bytes</td>
<td>-2147483648 to 2147483647</td>
</tr>
<tr>
<td>unsigned int</td>
<td>4bytes</td>
<td>0 to 4294967295</td>
</tr>
<tr>
<td>signed int</td>
<td>4bytes</td>
<td>-2147483648 to 2147483647</td>
</tr>
<tr>
<td>short int</td>
<td>2bytes</td>
<td>-32768 to 32767</td>
</tr>
<tr>
<td>unsigned short int</td>
<td>2bytes</td>
<td>0 to 65,535</td>
</tr>
<tr>
<td>signed short int</td>
<td>2bytes</td>
<td>-32768 to 32767</td>
</tr>
<tr>
<td>long int</td>
<td>8bytes</td>
<td>-2,147,483,648 to 2,147,483,647</td>
</tr>
<tr>
<td>signed long int</td>
<td>8bytes</td>
<td>same as long int</td>
</tr>
<tr>
<td>unsigned long int</td>
<td>8bytes</td>
<td>0 to 4,294,967,295</td>
</tr>
<tr>
<td>long long int</td>
<td>8bytes</td>
<td>-(2^63) to (2^63)-1</td>
</tr>
<tr>
<td>unsigned long long int</td>
<td>8bytes</td>
<td>0 to 18,446,744,073,709,551,615</td>
</tr>
<tr>
<td>float</td>
<td>4bytes</td>
<td></td>
</tr>
<tr>
<td>double</td>
<td>8bytes</td>
<td></td>
</tr>
<tr>
<td>long double</td>
<td>12bytes</td>
<td></td>
</tr>
<tr>
<td>wchar_t</td>
<td>2 or 4 bytes</td>
<td>1 wide character</td>
</tr>
</tbody></table>
<p>The size of variables might be different from those shown in the above table, depending on the compiler 
    and the computer you are using.</p>
    <h2>typedef Declarations</h2>
    <p>You can create a new name for an existing type using <b>typedef</b>. Following is the simple syntax to define a new type using typedef −</p>
    <div className='output'>
        <p><strong>Output:</strong></p>
        typedef type newname; 
    </div>
    <p>For example, the following tells the compiler that feet is another name for int −</p>
    <div class="output">typedef int feet;
</div>
<p>Now, the following declaration is perfectly legal and creates an integer variable called distance −</p>
<div class="output">feet distance;
</div>
<h2>Enumerated Types</h2>
<p>An enumerated type declares an optional type name  and a set of zero or more identifiers that can be used as values of the type. Each enumerator is a constant whose type is the enumeration.</p>
<p>Creating an enumeration requires the use of the keyword <b>enum</b>. The general form of an enumeration type is −</p>
<div class="output">enum enum-name &#123; list of names &#125; var-list; 
</div>
<p>Here, the enum-name is the enumeration's type name. The list of names is comma separated.</p>
<p>For example, the following code defines an enumeration of colors called colors and the variable c of type color. Finally, c is assigned the value "blue".</p>
<div class="output">enum color &#123; red, green, blue &#125; c; <br />
c = blue;
</div>
<p>By default, the value of the first name is 0, the second name has the value 1, and the third has the value 2, and so on. But you can give a name, a specific value by adding an initializer. For example, in the following enumeration, <b>green</b> will have the value 5.</p>
<div class="result notranslate">enum color &#123; red, green = 5, blue  &#125;;
</div>
<p>Here, <b>blue</b> will have a value of 6 because each name will be one greater than the one that precedes it.</p>
        </div>
    )
}

export default D_t
